import { getNextLevelData } from '@/api/base/api'

/**
 * 地区选择工具类
 * 提供省市县三级联动的通用功能
 */
class RegionUtils {
  static loadRegion(node, resolve, type = 'REGION_CODE') {
    const { level, value } = node
    const dictParentCode = level === 0 ? '0' : value
    
    getNextLevelData({
      dictType: type,
      parentCode: dictParentCode
    }).then(res => {
      // 兼容 code: 0 和 code: 200 两种返回格式
      const isSuccess = res.code === 0 || res.code === 200
      if (!isSuccess) {
        console.error('接口返回错误:', res)
        resolve([])
        return
      }
      
      const list = Array.isArray(res.data) ? res.data : []
      const nodes = list.map(item => {
        // 更严格的 isLeaf 处理，确保布尔值正确
        const isLeafValue = item.isLeaf
        const isLeaf = isLeafValue === 1 || isLeafValue === '1' || isLeafValue === true
        
        return {
          value: item.dictCode,
          label: item.dictLabel,
          dictCode: item.dictCode,
          dictLabel: item.dictLabel,
          isLeaf: isLeaf
        }
      })
      
      resolve(nodes)
    }).catch(error => {
      console.error('获取下一级数据失败:', error)
      resolve([])
    })
  }
  static async handleRegionChange(value, cascaderRef, targetForm, options = {}, vueInstance = null, type = 'REGION_CODE') {
    const {
      provinceField = 'province',
      provinceCodeField = 'provinceCode',
      cityField = 'city',
      cityCodeField = 'cityCode',
      countyField = 'county',
      countyCodeField = 'countyCode',
      regionField = 'region',
      enableDebug = false
    } = options

    if (enableDebug) {
      // console.log('value:', value, 'type:', type)
    }

    if (!Array.isArray(value) || value.length === 0) {
      this.clearRegionFields(targetForm, options)
      return
    }

    // 设置 region 字段
    targetForm[regionField] = [...value]

    try {
      // 尝试从 cascader 组件获取节点信息
      if (cascaderRef && cascaderRef.getCheckedNodes && vueInstance) {
        await vueInstance.$nextTick()
        const checkedNodes = cascaderRef.getCheckedNodes()

        if (checkedNodes && checkedNodes.length > 0) {
          const selectedNode = checkedNodes[0]
          const pathNodes = selectedNode.pathNodes || []

          if (pathNodes.length > 0) {
            // 判断是否为标准的三级地区结构
            if (type === 'REGION_CODE') {
              // 标准三级地区结构：省市区
              const provinceNode = pathNodes[0] || {}
              const cityNode = pathNodes[1] || {}
              const countyNode = pathNodes[2] || {}

              targetForm[provinceField] = provinceNode.label || ''
              targetForm[provinceCodeField] = provinceNode.value || ''
              targetForm[cityField] = cityNode.label || ''
              targetForm[cityCodeField] = cityNode.value || ''
              targetForm[countyField] = countyNode.label || ''
              targetForm[countyCodeField] = countyNode.value || ''
            } else {
              // 非标准结构（如行业类型）：获取所有级别的节点信息
              // 支持2级或3级结构
              const level1Node = pathNodes[0] || {}
              const level2Node = pathNodes[1] || {}
              const level3Node = pathNodes[2] || {}
              
              // 设置一级信息
              targetForm[provinceField] = level1Node.label || ''
              targetForm[provinceCodeField] = level1Node.value || ''
              
              // 设置二级信息
              targetForm[cityField] = level2Node.label || ''
              targetForm[cityCodeField] = level2Node.value || ''
              
              // 设置三级信息（如果存在）
              targetForm[countyField] = level3Node.label || ''
              targetForm[countyCodeField] = level3Node.value || ''
            }

            if (enableDebug) {
              // console.log('获取节点信息成功, type:', type)
              if (type !== 'REGION_CODE') {
                // console.log('最后节点:', targetForm[countyField], targetForm[countyCodeField])
              }
            }
            return
          }
        }
      }

      // 备用方案：通过接口获取名称
      await this.getRegionNamesByCode(value, targetForm, options, type)

    } catch (error) {
      // console.error('获取节点信息失败:', error)
      await this.getRegionNamesByCode(value, targetForm, options, type)
    }
  }

  static async getRegionNamesByCode(codes, targetForm, options = {}, type = 'REGION_CODE') {
    const {
      provinceField = 'province',
      provinceCodeField = 'provinceCode',
      cityField = 'city',
      cityCodeField = 'cityCode',
      countyField = 'county',
      countyCodeField = 'countyCode',
      enableDebug = false
    } = options

    if (enableDebug) {
      console.log('通过编码获取名称:', codes, 'type:', type)
    }

    try {
      if (type === 'REGION_CODE') {
        // 标准三级地区结构处理
        // 设置编码
        targetForm[provinceCodeField] = codes[0] || ''
        targetForm[cityCodeField] = codes[1] || ''
        targetForm[countyCodeField] = codes[2] || ''

        // 获取省名称
        if (codes[0]) {
          const provinceRes = await getNextLevelData({
            dictType: 'REGION_CODE',
            parentCode: '0'
          })
          const provinceList = Array.isArray(provinceRes.data) ? provinceRes.data : []
          const province = provinceList.find(item => item.dictCode === codes[0])
          if (province) {
            targetForm[provinceField] = province.dictLabel
            if (enableDebug) {
              // console.log('找到省名称:', province.dictLabel)
            }
          }
        }

        // 获取市名称
        if (codes[1]) {
          const cityRes = await getNextLevelData({
            dictType: 'REGION_CODE',
            parentCode: codes[0]
          })
          const cityList = Array.isArray(cityRes.data) ? cityRes.data : []
          const city = cityList.find(item => item.dictCode === codes[1])
          if (city) {
            targetForm[cityField] = city.dictLabel
            if (enableDebug) {
              // console.log('找到市名称:', city.dictLabel)
            }
          }
        }

        // 获取县名称
        if (codes[2]) {
          const countyRes = await getNextLevelData({
            dictType: 'REGION_CODE',
            parentCode: codes[1]
          })
          const countyList = Array.isArray(countyRes.data) ? countyRes.data : []
          const county = countyList.find(item => item.dictCode === codes[2])
          if (county) {
            targetForm[countyField] = county.dictLabel
            if (enableDebug) {
              // console.log('找到县名称:', county.dictLabel)
            }
          }
        }
        
        if (enableDebug) {
          // console.log('province:', targetForm[provinceField], 'provinceCode:', targetForm[provinceCodeField])
          // console.log('city:', targetForm[cityField], 'cityCode:', targetForm[cityCodeField])
          // console.log('county:', targetForm[countyField], 'countyCode:', targetForm[countyCodeField])
        }
      } else {
        // 非标准结构：只获取最后一个节点的名称
        const lastCode = codes[codes.length - 1]
        if (lastCode && codes.length > 1) {
          const parentCode = codes[codes.length - 2]
          const lastRes = await getNextLevelData({
            dictType: type,
            parentCode: parentCode
          })
          const lastList = Array.isArray(lastRes.data) ? lastRes.data : []
          const lastItem = lastList.find(item => item.dictCode === lastCode)
          if (lastItem) {
            // 清空其他字段
            targetForm[provinceField] = ''
            targetForm[provinceCodeField] = ''
            targetForm[cityField] = ''
            targetForm[cityCodeField] = ''
            
            // 只设置最后一个节点
            targetForm[countyField] = lastItem.dictLabel
            targetForm[countyCodeField] = lastCode
            
            if (enableDebug) {
              // console.log('找到最后节点:', lastItem.dictLabel, lastCode)
            }
          }
        }
      }

    } catch (error) {
      // console.error('通过编码获取地区名称失败:', error)
    }
  }

  //  清空地区相关字段
  static clearRegionFields(targetForm, options = {}) {
    const {
      provinceField = 'province',
      provinceCodeField = 'provinceCode',
      cityField = 'city',
      cityCodeField = 'cityCode',
      countyField = 'county',
      countyCodeField = 'countyCode',
      regionField = 'region'
    } = options

    targetForm[provinceField] = ''
    targetForm[provinceCodeField] = ''
    targetForm[cityField] = ''
    targetForm[cityCodeField] = ''
    targetForm[countyField] = ''
    targetForm[countyCodeField] = ''
    targetForm[regionField] = []
  }

  // 获取 el-cascader 的配置选项
  static getCascaderProps(type = 'REGION_CODE') {
    return {
      lazy: true,
      checkStrictly: false,
      emitPath: true,
      value: 'value',
      label: 'label',
      leaf: 'isLeaf', // 明确指定叶子节点字段
      lazyLoad: (node, resolve) => this.loadRegion(node, resolve, type)
    }
  }

  // 初始化表单的地区字段
  static initRegionFields(form, options = {}) {
    const {
      provinceField = 'province',
      provinceCodeField = 'provinceCode',
      cityField = 'city',
      cityCodeField = 'cityCode',
      countyField = 'county',
      countyCodeField = 'countyCode',
      regionField = 'region'
    } = options

    form[provinceField] = form[provinceField] || ''
    form[provinceCodeField] = form[provinceCodeField] || ''
    form[cityField] = form[cityField] || ''
    form[cityCodeField] = form[cityCodeField] || ''
    form[countyField] = form[countyField] || ''
    form[countyCodeField] = form[countyCodeField] || ''
    form[regionField] = form[regionField] || []
  }
}

export default RegionUtils
